import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Calendar, Clock, MapPin, Save, X, Upload } from 'lucide-react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { uploadImage, generateImagePath } from '../services/imageUploadService';

interface Event {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  description: string;
  image: string;
  category: string;
  price?: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

export default function EventsNew() {
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    endDate: '',
    endTime: '',
    location: '',
    description: '',
    image: '',
    category: '',
    price: '',
    coordinates: {
      latitude: 0,
      longitude: 0
    }
  });

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setIsLoading(true);
      console.log('📅 Caricando eventi...');
      const eventsCol = collection(db, 'events');
      const eventsSnapshot = await getDocs(eventsCol);
      const eventsData = eventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Event[];
      
      console.log(`📅 Trovati ${eventsData.length} eventi in Firestore`);
      
      if (eventsData.length === 0) {
        console.log('⚠️ Nessun evento in Firestore, usando dati hardcoded');
        // Dati hardcoded di fallback
        const hardcodedEvents = [
          {
            id: '1',
            title: 'La sagra della mozzarella',
            date: '2024-08-15',
            time: '18:00',
            location: 'Casal Velino',
            description: 'Vieni a mangiare la mozzarella!',
            image: '/placeholder-event.jpg',
            category: 'Caseifici',
            price: 'Gratuito',
            coordinates: {
              latitude: 40.1691,
              longitude: 15.1430
            }
          }
        ];
        setEvents(hardcodedEvents);
      } else {
        console.log('✅ Eventi caricati da Firestore');
        setEvents(eventsData);
      }
    } catch (error) {
      console.error('❌ Error loading events:', error);
      setEvents([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectEvent = (event: Event) => {
    setSelectedEvent(event);
    setFormData({
      title: event.title,
      date: event.date,
      time: event.time,
      endDate: (event as any).endDate || '',
      endTime: (event as any).endTime || '',
      location: event.location,
      description: event.description,
      image: event.image,
      category: event.category,
      price: event.price || '',
      coordinates: event.coordinates || { latitude: 0, longitude: 0 }
    });
  };

  const handleNew = () => {
    setSelectedEvent(null);
    setFormData({
      title: '',
      date: '',
      time: '',
      endDate: '',
      endTime: '',
      location: '',
      description: '',
      image: '',
      category: '',
      price: '',
      coordinates: {
        latitude: 0,
        longitude: 0
      }
    });
  };

  const handleCancel = () => {
    if (selectedEvent) {
      setFormData({
        title: selectedEvent.title,
        date: selectedEvent.date,
        time: selectedEvent.time,
        endDate: (selectedEvent as any).endDate || '',
        endTime: (selectedEvent as any).endTime || '',
        location: selectedEvent.location,
        description: selectedEvent.description,
        image: selectedEvent.image,
        category: selectedEvent.category,
        price: selectedEvent.price || '',
        coordinates: selectedEvent.coordinates || { latitude: 0, longitude: 0 }
      });
    } else {
      setFormData({
        title: '',
        date: '',
        time: '',
        endDate: '',
        endTime: '',
        location: '',
        description: '',
        image: '',
        category: '',
        price: '',
        coordinates: {
          latitude: 0,
          longitude: 0
        }
      });
    }
  };


  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const imagePath = generateImagePath('events', selectedEvent?.id || 'new', file.name);
      const imageUrl = await uploadImage(file, imagePath);
      setFormData({ ...formData, image: imageUrl });
      console.log('Immagine caricata con successo:', imageUrl);
    } catch (error) {
      console.error('Errore durante il caricamento dell\'immagine:', error);
      alert('Errore durante il caricamento dell\'immagine');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Rimuovi campi undefined prima di salvare
      const cleanData: any = {};
      Object.keys(formData).forEach(key => {
        const value = (formData as any)[key];
        if (value !== undefined && value !== '') {
          cleanData[key] = value;
        }
      });
      
      // Assicurati che i campi required siano presenti
      if (!cleanData.title || !cleanData.date || !cleanData.time || !cleanData.location) {
        alert('Compilare tutti i campi obbligatori: Titolo, Data, Ora, Luogo');
        return;
      }
      
      if (selectedEvent) {
        await updateDoc(doc(db, 'events', selectedEvent.id), cleanData);
      } else {
        await addDoc(collection(db, 'events'), cleanData);
      }
      await loadEvents();
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Errore durante il salvataggio dell\'evento');
    }
  };

  const handleDelete = async (eventId: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questo evento?')) {
      try {
        await deleteDoc(doc(db, 'events', eventId));
        await loadEvents();
      } catch (error) {
        console.error('Error deleting event:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-primary text-xl">Caricamento eventi...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-primary text-white p-6">
        <div className="container mx-auto max-w-7xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Calendar size={24} />
              <div>
                <h1 className="text-2xl font-bold">Gestione Eventi</h1>
                <p className="text-white/80 text-sm mt-1">
                  Gestisci gli eventi dell'app mobile T-Alento
                </p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleNew}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <Plus size={16} />
                <span>Nuovo Evento</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-7xl px-4 py-8">

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Lista Eventi */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Eventi ({events.length})</h2>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {events.map((event) => (
                <div
                  key={event.id}
                  className={`p-4 rounded-lg cursor-pointer transition-colors duration-200 ${
                    selectedEvent?.id === event.id
                      ? 'bg-primary/10 border-2 border-primary'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                  onClick={() => handleSelectEvent(event)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800">{event.title}</h3>
                      <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                        <div className="flex items-center space-x-1">
                          <Calendar size={14} />
                          <span>{event.date}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock size={14} />
                          <span>{event.time}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <MapPin size={14} />
                          <span>{event.location}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectEvent(event);
                        }}
                        className="p-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 rounded-lg transition-colors duration-200"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(event.id);
                        }}
                        className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-200 rounded-lg transition-colors duration-200"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form Evento */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              {selectedEvent ? 'Modifica Evento' : 'Nuovo Evento'}
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Titolo</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Titolo dell'evento"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Data Inizio</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Ora Inizio</label>
                  <input
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Data Fine (opzionale)</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Ora Fine (opzionale)</label>
                  <input
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Luogo</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Luogo dell'evento"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Categoria</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Categoria dell'evento"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Prezzo</label>
                <input
                  type="text"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Prezzo (es. Gratuito, 15€)"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Descrizione</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Descrizione dell'evento"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Immagine</label>
                <div className="space-y-2">
                  <label className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-white/20 hover:bg-white/30 border border-white/30 rounded-lg cursor-pointer transition-colors">
                    <Upload size={20} className="text-white" />
                    <span className="text-white font-medium">
                      {isUploading ? 'Caricamento...' : 'Carica Immagine'}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={isUploading}
                    />
                  </label>
                  <input
                    type="url"
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Oppure inserisci URL dell'immagine"
                  />
                  {formData.image && (
                    <div className="mt-2">
                      <img 
                        src={formData.image} 
                        alt="Anteprima" 
                        className="w-32 h-20 object-cover rounded-lg"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  onClick={handleSave}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <Save size={16} />
                  <span>Salva</span>
                </button>
                <button
                  onClick={handleCancel}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <X size={16} />
                  <span>Annulla</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
